import React from 'react';
import { faBriefcase } from '@fortawesome/free-solid-svg-icons';
import { iconOptions } from '@frontend/app/containers/Projects/CampaignPage/components/ImageIconSelector';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import xss from 'xss';
import { ViewMode } from '../../constants';

interface PersonaProps {
  view: ViewMode;
  image: string;
  title: string;
  items: Array<{ text: string; icon?: string }>; // Each item has text and optional icon
  listStyleType?: 'number' | 'bullet' | 'icon' | 'none';
  backgroundColor?: string;
  titleColor?: string;
  titleFontSize?: number;
  titleFontFamily?: string;
  bodyTextColor?: string;
  bodyTextFontSize?: number;
  bodyTextFontFamily?: string;
}

export function Persona({
  view,
  image,
  title,
  items,
  listStyleType = 'bullet',
  backgroundColor = '#f8f8f5',
  titleColor = '#1a232b',
  titleFontSize = 24,
  titleFontFamily = 'Inter, sans-serif',
  bodyTextColor = '#1a232b',
  bodyTextFontSize = 18,
  bodyTextFontFamily = 'Inter, sans-serif',
}: PersonaProps) {
  const titleStyle = {
    color: titleColor,
    fontSize: titleFontSize,
    fontFamily: titleFontFamily,
    fontWeight: 600,
    textAlign: 'center' as const,
  };

  const bodyStyle = {
    color: bodyTextColor,
    fontSize: bodyTextFontSize,
    fontFamily: bodyTextFontFamily,
  };

  function renderPrefix(idx: number, item: { text: string; icon?: string }) {
    switch (listStyleType) {
      case 'number':
        return (
          <span className="inline-flex items-center justify-center w-8 h-8 rounded bg-white text-black font-semibold">
            {idx + 1}
          </span>
        );
      case 'bullet':
        return (
          <span className="text-lg mt-1 mr-2 font-bold" style={{ color: bodyTextColor }}>•</span>
        );
      case 'icon': {
        const iconObj = iconOptions.find((icon) => icon.name === item.icon);
        const iconToUse = iconObj ? iconObj.icon : faBriefcase;
        return (
          <FontAwesomeIcon icon={iconToUse} className="w-5 h-5 align-middle text-grey-3 mr-2" />
        );
      }
      case 'none':
      default:
        return null;
    }
  }

  if (view === ViewMode.DESKTOP) {
    return (
      <section className="w-full flex flex-row bg-white" style={{ background: backgroundColor }}>
        <div className="flex-1 flex items-center justify-end">
          <img
            src={image}
            alt="Persona"
            className="object-cover rounded-none"
            style={{ width: '100%', maxWidth: 520, aspectRatio: '4/5', height: 'auto' }}
          />
        </div>
        <div className="flex-1 flex flex-col justify-center px-16 py-12" style={{ background: backgroundColor }}>
          <div
            className="mb-8 w-full break-words text-left"
            style={{ ...titleStyle, textAlign: 'left' }}
          >
            {title}
          </div>
          <ul key="desktop-persona-list" className="flex flex-col gap-6">
            {items.map((item, idx) => (
              <li key={`desktop-${idx}-${item.text.substring(0, 20)}`} className="flex items-start gap-3">
                {renderPrefix(idx, item)}
                <span
                  className="w-full break-words"
                  style={{ ...bodyStyle, overflowWrap: 'anywhere', wordBreak: 'break-word', textAlign: 'left' }}
                  dangerouslySetInnerHTML={{ __html: xss(item.text) }}
                />
              </li>
            ))}
          </ul>
        </div>
      </section>
    );
  }

  // Mobile view
  return (
    <section className="w-full flex flex-col items-center bg-white">
      <img
        src={image}
        alt="Persona"
        className="w-full aspect-[4/5] object-cover"
        style={{ maxHeight: 320 }}
      />
      <div
        className="w-full px-4 py-8"
        style={{ background: backgroundColor }}
      >
        <div
          className="mb-6 w-full break-words text-center"
          style={titleStyle}
        >
          {title}
        </div>
        <ul key="mobile-persona-list" className="flex flex-col gap-4">
          {items.map((item, idx) => (
            <li key={`mobile-${idx}-${item.text.substring(0, 20)}`} className="flex items-start gap-2">
              {renderPrefix(idx, item)}
              <span
                className="w-full break-words"
                style={{ ...bodyStyle, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                dangerouslySetInnerHTML={{ __html: xss(item.text) }}
              />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
